import { BigNumber } from 'ethers';

import { IWalletCurrencyDetails } from 'models/wallet-currency.interface';

import { ERC20_ABI } from 'constants/abi/erc20-abi.constant';
import { DEFAULTS } from 'constants/defaults.constant';

import { BaseWeb3Service } from './base-web3.service';

/**
 * Service for handling ERC20 token interactions
 * Manages balance checks, permissions, and approvals
 */
export class Erc20Service extends BaseWeb3Service {
  private readonly erc20Abi = ERC20_ABI;

  /**
   * Gets the balance of a currency for an account
   */
  async getBalance(currencyAddress: string, accountAddress: string): Promise<string> {
    const contract = this.createContract(this.erc20Abi, currencyAddress);
    const balance = await contract.methods.balanceOf(accountAddress).call();

    return balance as string;
  }

  /**
   * Checks if an account has permission to spend currency on behalf of the contract
   */
  async checkPermission(
    spenderAddress: string,
    walletAddress: string,
    currency: Pick<IWalletCurrencyDetails, 'address'>,
    requiredAmount: number
  ): Promise<boolean> {
    const contract = this.createContract(this.erc20Abi, currency.address);

    const allowance = await contract.methods.allowance(walletAddress, spenderAddress).call();

    return +allowance >= requiredAmount;
  }

  /**
   * Grants permission for a contract to spend currency with recommended limit
   */
  async grantPermission(
    contractAddress: string,
    currency: Pick<IWalletCurrencyDetails, 'address' | 'decimal'>,
    accountAddress: string
  ): Promise<void> {
    const recommendedPaymentLimit = BigNumber.from(10)
      .pow(currency.decimal)
      .mul(DEFAULTS.defaultRecommendedPaymentLimit)
      .toString();

    const contract = this.createContract(this.erc20Abi, currency.address);
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    await contract.methods.approve(contractAddress, recommendedPaymentLimit).send({
      from: accountAddress,
      gasPrice,
    });
  }

  /**
   * Grants permission for a contract to spend a specific amount of currency
   */
  async grantPermissionWithAmount(
    contractAddress: string,
    currency: IWalletCurrencyDetails,
    accountAddress: string,
    amount: string
  ): Promise<void> {
    const contract = this.createContract(this.erc20Abi, currency.address);
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    await contract.methods.approve(contractAddress, amount).send({
      from: accountAddress,
      gasPrice,
    });
  }

  async transfer(
    tokenAddress: string,
    toAddress: string,
    amount: string,
    fromAddress: string
  ): Promise<string> {
    const contract = this.createContract(this.erc20Abi, tokenAddress);
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    return new Promise((resolve, reject) => {
      contract.methods
        .transfer(toAddress, amount)
        .send({ from: fromAddress, gasPrice })
        .on('transactionHash', (hash: string) => {
          resolve(hash);
        })
        .on('error', (error: Error) => {
          reject(error);
        });
    });
  }

  /**
   * Grants permission using either specific amount or recommended limit
   * Uses specific amount if it exceeds the recommended payment limit
   */
  async grantPermissionWrapper(
    contractAddress: string,
    currency: IWalletCurrencyDetails,
    accountAddress: string,
    amount: string
  ): Promise<void> {
    const recommendedPaymentLimit = BigNumber.from(10)
      .pow(currency.decimal)
      .mul(DEFAULTS.defaultRecommendedPaymentLimit)
      .toString();

    const amountBN = BigNumber.from(amount);

    if (amountBN.gt(recommendedPaymentLimit)) {
      return await this.grantPermissionWithAmount(contractAddress, currency, accountAddress, amount);
    } else {
      return await this.grantPermission(contractAddress, currency, accountAddress);
    }
  }
}
